import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { IconComponent } from '@backstage/core-plugin-api';
import LogoFull from './Root/LogoFull';
import { Apps, Cloud, Home, Search, LibraryBooks, Widgets, Storage, ViewModule } from '@material-ui/icons';
import { FileCode2, PlusCircle } from 'lucide-react';

interface SubNavItem {
  title: string;
  to: string;
  icon?: IconComponent;
}

interface NavItem {
  title: string;
  to?: string;
  icon?: IconComponent;
  subItems?: SubNavItem[];
}

interface CustomNavBarProps {
  navItems?: NavItem[];
}

export const CustomNavBar: React.FC<CustomNavBarProps> = () => {
  const location = useLocation();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Business/Core navigation items with dropdown support
  const businessNavItems: NavItem[] = [
    { title: 'My dashboard', to: '/', icon: Home as IconComponent },
    { 
      title: 'Products', 
      icon: Apps as IconComponent,
      subItems: [
        { title: 'Workloads', to: '/catalog?filters[kind]=System', icon: Apps as IconComponent },
        { title: 'Code Libraries', to: '/catalog?filters[kind]=Component&filters[type]=library', icon: LibraryBooks as IconComponent },
        { title: 'APIs', to: '/api-docs', icon: Widgets as IconComponent }
      ]
    },
    { 
      title: 'Cloud', 
      icon: Cloud as IconComponent,
      subItems: [
        { title: 'Resource Modules', to: '/catalog?filters[kind]=Component&filters[type]=resource', icon: Storage as IconComponent },
        { title: 'Pattern Modules', to: '/catalog?filters[kind]=Component&filters[type]=pattern', icon: ViewModule as IconComponent }
      ]
    },
    { title: 'Docs', to: '/docs', icon: FileCode2 as IconComponent },
  ];

  // Utility/Action items
  const utilityNavItems: NavItem[] = [
    { title: 'Create', to: '/create', icon: PlusCircle as IconComponent },
    { title: 'Search', to: '/search', icon: Search as IconComponent },
  ];

  // Style constants
  const styles = {
    business: {
      base: 'inline-flex items-center px-3 py-2 text-sm font-medium border-b-2 transition-colors duration-200 relative',
      active: 'border-blue-500 text-gray-900',
      inactive: 'border-transparent text-gray-900 hover:text-gray-700 hover:border-gray-300'
    },
    utility: {
      base: 'inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200',
      active: 'bg-blue-100 text-blue-700 border border-blue-200',
      inactive: 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 border border-transparent'
    },
    mobile: {
      active: 'bg-blue-50 border-blue-500 text-blue-700',
      inactive: 'border-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-900'
    },
    dropdown: {
      container: 'absolute top-full left-0 -mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-[9999] pt-2',
      item: {
        base: 'flex items-center px-4 py-2 text-sm transition-colors duration-200',
        active: 'bg-blue-50 text-blue-700',
        inactive: 'text-gray-700 hover:bg-gray-100'
      }
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      let clickedInside = false;
      
      Object.values(dropdownRefs.current).forEach(ref => {
        if (ref && ref.contains(target)) {
          clickedInside = true;
        }
      });
      
      if (!clickedInside) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMouseEnter = (itemTitle: string) => {
    setOpenDropdown(itemTitle);
  };

  const handleMouseLeave = () => {
    setOpenDropdown(null);
  };

  const isActiveSubItem = (subItems: SubNavItem[]) => {
    return subItems.some(subItem => location.pathname.startsWith(subItem.to));
  };

  const getItemActiveState = (item: NavItem) => {
    return item.to ? location.pathname === item.to : isActiveSubItem(item.subItems || []);
  };

  const renderIcon = (icon: IconComponent | undefined, className: string) => {
    if (!icon) return null;
    return (
      <span className={className}>
        {React.createElement(icon)}
      </span>
    );
  };

  const renderSubItem = (subItem: SubNavItem, isMobile = false) => {
    const isActive = location.pathname.startsWith(subItem.to);
    const className = isMobile
      ? `${styles.mobile.active || styles.mobile.inactive} flex items-center pl-3 pr-4 py-2 border-l-4 text-sm font-medium`
      : `${styles.dropdown.item.base} ${isActive ? styles.dropdown.item.active : styles.dropdown.item.inactive}`;

    return (
      <Link
        key={subItem.to}
        to={subItem.to}
        className={className.replace('undefined', isActive ? styles.mobile.active : styles.mobile.inactive)}
        onClick={() => setOpenDropdown(null)}
      >
        {renderIcon(subItem.icon, isMobile ? "mr-2 inline-flex items-center justify-center w-4 h-4" : "mr-2 inline-flex items-center justify-center w-4 h-4")}
        {subItem.title}
      </Link>
    );
  };

  const renderNavItem = (item: NavItem, isUtility = false) => {
    const hasSubItems = item.subItems && item.subItems.length > 0;
    const isActive = getItemActiveState(item);
    const isDropdownOpen = openDropdown === item.title;
    
    const styleConfig = isUtility ? styles.utility : styles.business;
    const baseClasses = styleConfig.base;
    const activeClasses = styleConfig.active;
    const inactiveClasses = styleConfig.inactive;

    if (hasSubItems) {
      return (
        <div 
          key={item.title}
          className="relative"
          ref={el => dropdownRefs.current[item.title] = el}
          onMouseEnter={() => handleMouseEnter(item.title)}
          onMouseLeave={handleMouseLeave}
        >
          <button
            className={`${baseClasses} ${isActive ? activeClasses : inactiveClasses} cursor-pointer bg-transparent border-none h-16`}
          >
            {renderIcon(item.icon, "mr-1.5 inline-flex items-center justify-center w-4 h-4")}
            {item.title}
          </button>
          
          {isDropdownOpen && (
            <div className={styles.dropdown.container}>
              {item.subItems?.map((subItem) => renderSubItem(subItem, false))}
            </div>
          )}
        </div>
      );
    }

    // Regular nav item without dropdown
    return (
      <Link
        key={item.to}
        to={item.to!}
        className={`${baseClasses} ${isActive ? activeClasses : inactiveClasses}`}
      >
        {renderIcon(item.icon, "mr-1.5 inline-flex items-center justify-center w-4 h-4")}
        {item.title}
      </Link>
    );
  };

  return (
    <nav className="bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo and Main Navigation */}
          <div className="flex">
            {/* Appvia Logo */}
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="flex items-center">
                <LogoFull />
              </Link>
            </div>

            {/* Business Navigation */}
            <div className="hidden sm:ml-8 sm:flex sm:space-x-1">
              {businessNavItems.map((item) => renderNavItem(item, false))}
            </div>
          </div>

          {/* Utility Navigation */}
          <div className="hidden sm:flex sm:items-center sm:space-x-2">
            <div className="flex items-center px-3 py-1 bg-gray-50 rounded-lg border">
              {utilityNavItems.map((item, index) => (
                <React.Fragment key={item.to}>
                  {renderNavItem(item, true)}
                  {index < utilityNavItems.length - 1 && (
                    <div className="mx-2 h-4 w-px bg-gray-300"></div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
  };
